import * as React from "react";
import moment from "moment";
import { Carousel } from "../../../Common/Carousel/Carousel";
import { CancellablePromise } from "../../../Utilities/Generic/Promises";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { LoginNewsItem, type LoginNewsItemType } from "./LoginNewsItem";

interface LoginNewsPost extends LoginNewsItemType {
    id: string;
}

interface State {
    loading: boolean;
    news: Array<LoginNewsPost>;
}

export default class LoginNews extends React.Component {
    public newsPromise: CancellablePromise<Array<LoginNewsPost>> | undefined;

    public state: State = {
        loading: false,
        news   : [],
    };

    public componentDidMount(): void {
        this.loadNews();
    }

    public componentWillUnmount(): void {
        if(this.newsPromise) {
            this.newsPromise.cancel();
        }
    }

    public loadNews = (): Promise<void> => {
        if(process.env.NODE_ENV === "test") {
            // Currently just don't attempt to load the news in the test environment
            // In the future, it's fine to remove this and mock the request instead
            return Promise.resolve();
        }

        this.setState({
            loading: true,
            news   : [],
        });

        const latestDate = moment().subtract(1, "month");

        this.newsPromise = new CancellablePromise(
            fetch(`https://wp.shopfront.com.au/wp-json/wp/v2/posts?page=1&per_page=3&after=${latestDate.toISOString()}`)
                .then(response => response.json())
                .then(async posts => {
                    const formatted = [];
                    const images: Record<string, false | string> = {};
                    const includes = [];

                    for(let i = 0, l = posts.length; i < l; i++) {
                        formatted.push({
                            id   : posts[i].id,
                            url  : `https://shopfront.com.au/blog/post/${posts[i].id}`,
                            image: posts[i].featured_media,
                            text : this.parseNewsTitle(posts[i].title.rendered),
                            date : moment(posts[i].date),
                        });

                        images[posts[i].featured_media] = false;
                        includes.push(`include[]=${posts[i].featured_media}`);
                    }

                    const loadedImages = await fetch(`https://wp.shopfront.com.au/wp-json/wp/v2/media?${includes.join("&")}`)
                        .then(response => response.json());

                    for(let i = 0, l = loadedImages.length; i < l; i++) {
                        let url = loadedImages[i].guid.rendered;

                        if(loadedImages[i]?.media_details?.sizes?.full?.source_url) {
                            url = loadedImages[i].media_details.sizes.full.source_url;
                        }

                        images[loadedImages[i].id] = url as string;
                    }

                    for(let i = 0, l = formatted.length; i < l; i++) {
                        formatted[i].image = images[formatted[i].image];
                    }

                    return formatted;
                })
        );

        return this.newsPromise
            .then(formatted => {
                this.setState({
                    loading: false,
                    news   : formatted,
                });
            })
            .catch(err => {
                if(err.cancelled) {
                    return;
                }

                console.error("Error while parsing or fetching the news", err);

                this.setState({
                    loading: false,
                });
            });
    };

    protected parseNewsTitle = (title: string): string => {
        const el = document.createElement("textarea");

        // The text inside a "textarea" tag doesn't get executed
        el.innerHTML = title;

        return el.value;
    };

    public render(): React.ReactNode {
        if(this.state.loading) {
            return (
                <div className="loading" key="loading">
                    <FontAwesomeLight icon="spinner-third" spin />
                </div>
            );
        } else if(this.state.news.length === 0) {
            return (
                <div className="no-news" key="no-news">
                    <div><FontAwesomeLight icon="newspaper" /></div>
                    <p>We have no news to show you at this time.</p>
                </div>
            );
        }

        return (
            <Carousel changeTime={10000}>
                {this.state.news.map((item) => (
                    <LoginNewsItem key={item.id} {...item} />
                ))}
            </Carousel>
        );
    }
}
